import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { InfoIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useImportTranslations } from '@/hooks/useImportTranslations';

export type ImportMode = 'products' | 'structure' | 'bulk_images';

interface StepImportModeProps {
  onModeSelected: (mode: ImportMode) => void;
}

// Define type for import mode options
export interface ImportModeOption {
  value: ImportMode;
  label: string;
  description: string;
  tooltip?: string;
}

export function StepImportMode({ onModeSelected }: StepImportModeProps) {
  const [selectedMode, setSelectedMode] = React.useState<ImportMode>('products');

  // Initialize translations
  const { getModeText, getWizardText, getButtonText, t } = useImportTranslations();

  // Define import modes using i18n
  const importModes: ImportModeOption[] = [
    {
      value: 'products',
      label: getModeText('products', 'title'),
      description: getModeText('products', 'description'),
      tooltip: getModeText('products', 'instructions'),
    },
    {
      value: 'structure',
      label: getModeText('structure', 'title'),
      description: getModeText('structure', 'description'),
      tooltip: getModeText('structure', 'instructions'),
    },
    {
      value: 'bulk_images',
      label: getModeText('bulk_images', 'title'),
      description: getModeText('bulk_images', 'description'),
      tooltip: getModeText('bulk_images', 'instructions'),
    },
  ];

  const handleContinue = () => {
    onModeSelected(selectedMode);
  };

  return (
    <div className="space-y-6">
      <div className="mb-4 rounded-md bg-muted p-4">
        <h3 className="mb-2 font-medium">{getWizardText('importModeStep', 'title')}</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          {getWizardText('description', 'default')}
        </p>

        <RadioGroup
          value={selectedMode}
          onValueChange={value => setSelectedMode(value as ImportMode)}
          className="space-y-4"
          data-testid="import-mode-selection"
        >
          {importModes.map((mode: ImportModeOption) => (
            <Card
              key={mode.value}
              className={`border-2 p-4 ${selectedMode === mode.value ? 'border-primary' : 'border-muted'}`}
              data-testid={`import-mode-${mode.value}`}
            >
              <CardContent className="p-0">
                <div className="flex items-start space-x-2">
                  <RadioGroupItem
                    value={mode.value}
                    id={mode.value}
                    className="mt-1"
                    data-testid={`import-mode-radio-${mode.value}`}
                  />
                  <div className="flex-1">
                    <div className="flex items-center">
                      <Label htmlFor={mode.value} className="cursor-pointer text-base font-medium">
                        {mode.label}
                      </Label>
                      {mode.tooltip && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger className="ml-2">
                              <InfoIcon className="size-4 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs">{mode.tooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{mode.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </RadioGroup>

        <div className="mt-6">
          <Button onClick={handleContinue} data-testid="import-mode-continue-button">
            {getButtonText('next')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default StepImportMode;
